<script setup lang="ts">
import type { PhaseSteps } from "@/stores/listImport"
import { DialogSize, KDialog } from "@kaleidoscope-solutions/ks-design-system"
import confetti from "canvas-confetti"
import { storeToRefs } from "pinia"
import { computed, nextTick, onMounted, ref } from "vue"
import { openDialog } from "vue3-promise-dialog"
import WarningStateImage from "@/assets/pngs/WarningState.png?w=288&h=358&format=webp&imagetools"
import PictogramDialog from "@/components/Dialog/PictogramDialog.vue"

import DialogHeader from "@/components/ListImport/Dialog/DialogHeader.vue"
import Loading from "@/components/Loading.vue"
import messageHandler, { UIMessageKind } from "@/logic/message-handler"
import { useListImportStore } from "@/stores"
import { captureError } from "@/utils/useEngineModel"
import { useSnackbar } from "@/utils/useSnackbar"

const listImportStore = useListImportStore()
const { loading, stepper, allowImport } = storeToRefs(listImportStore)
const hasError = ref(false)

const stepComponent = computed(() => stepper.value.current.value.component ?? undefined)

onMounted(async () => {
  await listImportStore.getImportTargets()
})

const { addSnackbar } = useSnackbar()

function handleNext(step: PhaseSteps) {
  listImportStore.goToStep(step)
}

function handleStep(step: PhaseSteps) {
  listImportStore.goToStep(step)
}

async function handleImport() {
  try {
    await listImportStore.importDataIntoModel()
    await nextTick()

    closeImport()

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.2 },
      colors: ["#fd9a03", "#16a1a1", "#a414f7"],
    })
    messageHandler.emit(UIMessageKind.DataImported, undefined)
  } catch (error) {
    captureError(error)

    await openDialog<any>(PictogramDialog, {
      open: true,
      size: DialogSize.medium,
      cancelButton: undefined,
      okButton: "Close",
      imagePath: WarningStateImage,
      title: "Upload failed",
      hideCancel: true,
      body: [
        "We are sorry but it looks like something went wrong and we are not able to recover your progress.",
        "If you can remember the steps you took, note them down and let us know so we can work on it!",
      ],
    })
  }
}

function handleDisableImport(disableImport: boolean) {
  hasError.value = disableImport
}

const disableImport = computed(() => hasError.value || !allowImport.value)

function closeImport() {
  addSnackbar({
    type: "success",
    message: `Data imported.`,
  })

  listImportStore.closeDialog()
}
</script>

<template>
  <KDialog
    :open="true"
    :size="DialogSize.fullsize"
    :esc="false"
    @close="listImportStore.closeDialog"
  >
    <template #default>
      <DialogHeader
        :stepper="stepper"
        :disable-import="disableImport"
        @next="handleNext"
        @step="handleStep"
        @import="handleImport"
      >
        <slot>
          <div>
            <span>{{ listImportStore.file?.file.name }} </span>

            <button
              class="px-2 text-sm font-bold text-violet-700 hover:text-darkViolet"
              @click="listImportStore.fileSelect()"
            >
              Replace
            </button>
          </div>
        </slot>
      </DialogHeader>
      <div class="relative flex size-full flex-col overflow-hidden">
        <div
          v-if="loading"
          class="fixed inset-0 z-20 grid place-content-center"
        >
          <Loading class="size-40" />
        </div>
        <component
          :is="stepComponent"
          v-else-if="stepComponent"
          @disable-import="handleDisableImport"
        />
      </div>
    </template>
  </KDialog>
</template>
